import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import {
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  getProductionById,
  sendProductionForApproval,
  approveProduction,
  deleteProduction,
} from "../../../services/Export";
import ReceiptIcon from "@mui/icons-material/Receipt";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BuildIcon from "@mui/icons-material/Build";
import WarningIcon from "@mui/icons-material/Warning";
import SendIcon from "@mui/icons-material/Send";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import { RoleBasedRender } from "../../../components/RoleBaseRender";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Alert from "@mui/material/Alert";

function Ex_ProductionView() {
  const theme = useTheme();
  const history = useHistory();
  const { productionId } = useParams();
  const [production, setProduction] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  // Pending confirmation: one of "SEND" | "APPROVE" | "REJECT" | "DELETE"
  const [pendingAction, setPendingAction] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log("Fetching production data for ID:", productionId);
      const result = await getProductionById(productionId);
      if (result) {
        console.log("Production data:", result);
        setProduction(result);
      }
    } catch (error) {
      console.error("Error fetching production data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [productionId]);

  // ══════════════════════════════════════════════════════════════════
  // APPROVAL WORKFLOW ACTIONS
  // ══════════════════════════════════════════════════════════════════

  const ACTION_LABELS = {
    SEND: {
      title: "Send for approval",
      body: "This production will be sent to an authorized approver and can no longer be edited or deleted until they decide.",
      confirm: "Send for Approval",
    },
    APPROVE: {
      title: "Approve this production?",
      body: "Approving deducts the materials from import stock and adds the produced units to the product's warehouse stock. This cannot be undone.",
      confirm: "Approve",
    },
    REJECT: {
      title: "Reject this production?",
      body: "No materials will be consumed and no units will be added to the product.",
      confirm: "Reject",
    },
    DELETE: {
      title: "Delete this production?",
      body: "The production record will be removed permanently. It has not changed any stock, so nothing needs reversing.",
      confirm: "Delete",
    },
  };

  const runAction = async (action) => {
    setPendingAction(null);
    setActionError("");
    setLoading(true);

    let result;
    switch (action) {
      case "SEND":
        result = await sendProductionForApproval(productionId);
        break;
      case "APPROVE":
        result = await approveProduction(productionId, true);
        break;
      case "REJECT":
        result = await approveProduction(productionId, false);
        break;
      case "DELETE":
        result = await deleteProduction(productionId);
        break;
      default:
        setLoading(false);
        return;
    }

    if (result?.success) {
      if (action === "DELETE") {
        history.push("/template/ex_production");
        return;
      }
      await fetchData();
    } else {
      setLoading(false);
      if (result?.insufficientMaterials?.length > 0) {
        const shortfalls = result.insufficientMaterials
          .map(
            (m) =>
              `• ${m.materialName}: required ${m.required.toFixed(2)}, available ${m.available.toFixed(2)}, short by ${m.shortage.toFixed(2)}`,
          )
          .join("\n");
        setActionError(
          `Cannot approve — material stock is no longer sufficient:\n\n${shortfalls}\n\nRestock these materials and approve again.`,
        );
      } else {
        setActionError(result?.message || "Action failed. Please try again.");
      }
    }
  };

  const getStatusChipProps = (status) => {
    switch (status) {
      case "APPROVED":
        return { label: "Approved", color: "success" };
      case "REJECTED":
        return { label: "Rejected", color: "error" };
      case "AWAITING_APPROVAL":
        return { label: "Awaiting Approval", color: "info" };
      case "DRAFT":
        return { label: "Draft", color: "default" };
      default:
        return { label: status || "Unknown", color: "default" };
    }
  };

  const formatCurrency = (value) => {
    return typeof value === "number"
      ? `Rs.${value.toFixed(2)}`
      : `Rs.${parseFloat(value || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Loading production details...
        </Typography>
      </Box>
    );
  }

  if (!production) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No production data available
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </Paper>
    );
  }

  const totalCost =
    parseFloat(production.totalItemCost || 0) +
    parseFloat(production.labourCost || 0) +
    parseFloat(production.electricityCost || 0) +
    parseFloat(production.transportCost || 0) +
    parseFloat(production.otherCost || 0) +
    parseFloat(production.packingCost || 0);

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 },
        m: { xs: 1, md: 3 },
        borderRadius: 2,
        bgcolor: "#f9fafb",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ReceiptIcon fontSize="large" color="primary" />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="h5" fontWeight="600" color="primary.main">
                Production Details
              </Typography>
              <Chip size="small" {...getStatusChipProps(production.status)} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Production ID: {production.productionId}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {/* Draft — the creator sends it onward or discards it */}
          {production.status === "DRAFT" && (
            <>
              <RoleBasedRender
                allowedRoles={["PRODUCTION_CREATE", "ROLE_ADMIN"]}
              >
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => setPendingAction("SEND")}
                >
                  Send for Approval
                </Button>
              </RoleBasedRender>
              <RoleBasedRender
                allowedRoles={[
                  "PRODUCTION_DELETE",
                  "PRODUCTION_CREATE",
                  "ROLE_ADMIN",
                ]}
              >
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setPendingAction("DELETE")}
                >
                  Delete
                </Button>
              </RoleBasedRender>
            </>
          )}

          {/* Awaiting approval — only an approver can decide */}
          {production.status === "AWAITING_APPROVAL" && (
            <RoleBasedRender allowedRoles={["PRODUCTION_APPROVE", "ROLE_ADMIN"]}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckIcon />}
                onClick={() => setPendingAction("APPROVE")}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setPendingAction("REJECT")}
                sx={{ ml: 1 }}
              >
                Reject
              </Button>
            </RoleBasedRender>
          )}

          {/* Rejected — nothing left to do but clear it away */}
          {production.status === "REJECTED" && (
            <RoleBasedRender
              allowedRoles={[
                "PRODUCTION_DELETE",
                "PRODUCTION_CREATE",
                "ROLE_ADMIN",
              ]}
            >
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setPendingAction("DELETE")}
              >
                Delete
              </Button>
            </RoleBasedRender>
          )}

          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.goBack()}
          >
            Back to List
          </Button>
        </Box>
      </Box>

      {actionError && (
        <Alert
          severity="error"
          onClose={() => setActionError("")}
          sx={{ mb: 2, whiteSpace: "pre-line" }}
        >
          {actionError}
        </Alert>
      )}

      {production.status === "DRAFT" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This production is a draft and has not changed any stock yet. Its
          materials are deducted and the produced units are added only once an
          approver signs off.
        </Alert>
      )}

      {/* Action confirmation */}
      <Dialog open={!!pendingAction} onClose={() => setPendingAction(null)}>
        <DialogTitle>{ACTION_LABELS[pendingAction]?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {ACTION_LABELS[pendingAction]?.body}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAction(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={
              pendingAction === "REJECT" || pendingAction === "DELETE"
                ? "error"
                : "primary"
            }
            onClick={() => runAction(pendingAction)}
          >
            {ACTION_LABELS[pendingAction]?.confirm}
          </Button>
        </DialogActions>
      </Dialog>

      <Divider sx={{ mb: 4 }} />

      <Grid container spacing={3}>
        {/* Production Information Card */}
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight="700"
                sx={{ mb: 2, color: "primary.main" }}
              >
                Production Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Production Date
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {formatDate(production.productionDate)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Production Quantity
                  </Typography>
                  <Chip
                    label={`${production.productionQty} units`}
                    color="primary"
                    size="medium"
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Sold Quantity
                  </Typography>
                  <Chip
                    label={`${production.soldQuantity || 0} units`}
                    color={production.soldQuantity > 0 ? "success" : "default"}
                    size="medium"
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Remaining
                  </Typography>
                  <Chip
                    label={`${production.productionQty - (production.soldQuantity || 0)} units`}
                    color="info"
                    size="medium"
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Added By
                  </Typography>
                  <Typography variant="body1">{production.addedBy}</Typography>
                </Box>
                {production.approvedBy && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {production.status === "REJECTED"
                        ? "Rejected By"
                        : "Approved By"}
                    </Typography>
                    <Typography variant="body1">
                      {production.approvedBy}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Product Details Card */}
        <Grid item xs={12} md={8}>
          <Card elevation={2} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <InventoryIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Product Details
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Product Name
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {production.productName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Product Barcode
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {production.productBarcode || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Warehouse
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {production.warehouseName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    BOM Usage
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {production.useStandardBOM ? (
                      <Chip
                        label={`Standard BOM v${production.bomVersion || 1}`}
                        size="small"
                        color="success"
                        icon={<BuildIcon />}
                      />
                    ) : (
                      <Chip
                        label="Manual Entry"
                        size="small"
                        color="warning"
                        icon={<WarningIcon />}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Category Section */}
        {production.product?.ex_categories?.length > 0 && (
          <Grid item xs={12}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <CategoryIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    color="primary.main"
                  >
                    Product Categories
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {production.product.ex_categories.map((category, index) => (
                    <Chip
                      key={index}
                      label={
                        <Typography variant="body2">
                          <span style={{ fontWeight: "600" }}>
                            {category.category}
                          </span>
                          {category.subCat_1 && `: ${category.subCat_1}`}
                        </Typography>
                      }
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: 1,
                        py: 0.5,
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Production Materials Table */}
        <Grid item xs={12}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight="700"
                sx={{ mb: 2, color: "primary.main" }}
              >
                Production Materials ({production.items?.length || 0} items)
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                    >
                      <TableCell>
                        <strong>Material Name</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Item Code</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Unit Price</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Price Method</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Used Qty</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>BOM Standard Per Unit</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Override</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Total Cost</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {production.items?.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="500">
                            {item.materialName || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.materialCode || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(item.unitPrice)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.method || "N/A"}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="600">
                            {item.usingQty?.toFixed(2) || "0.00"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {item.bomStandardQty?.toFixed(2) || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {item.isManualOverride ? (
                            <Chip label="Manual" size="small" color="warning" />
                          ) : (
                            <Chip label="BOM" size="small" color="success" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="600">
                            {formatCurrency(item.unitPrice * item.usingQty)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!production.items || production.items.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No materials recorded
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Costs Breakdown Section */}
        <Grid item xs={12}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <AttachMoneyIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Cost Breakdown
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                      height: "100%",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Material Cost
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      color="primary.main"
                    >
                      {formatCurrency(production.totalItemCost)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.background.default, 0.6),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Labour
                    </Typography>
                    <Typography variant="body1" fontWeight="600">
                      {formatCurrency(production.labourCost)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.background.default, 0.6),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Electricity
                    </Typography>
                    <Typography variant="body1" fontWeight="600">
                      {formatCurrency(production.electricityCost)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.background.default, 0.6),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Transport
                    </Typography>
                    <Typography variant="body1" fontWeight="600">
                      {formatCurrency(production.transportCost)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.background.default, 0.6),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Other
                    </Typography>
                    <Typography variant="body1" fontWeight="600">
                      {formatCurrency(production.otherCost)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.background.default, 0.6),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Packing
                    </Typography>
                    <Typography variant="body1" fontWeight="600">
                      {formatCurrency(production.packingCost)}
                    </Typography>
                    {production.packingPersonName && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        Paid to: {production.packingPersonName}
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total Cost
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      color="success.main"
                    >
                      {formatCurrency(totalCost)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Notes Section */}
        {production.note && (
          <Grid item xs={12}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  sx={{ mb: 1, color: "primary.main" }}
                >
                  Production Notes
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {production.note}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}

Ex_ProductionView.propTypes = {
  productionId: PropTypes.string,
};

export default Ex_ProductionView;
